import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/http";
import { useAuth } from "@/context/AuthContext";
import { fmtCurrency } from "@/lib/calc";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Package, AlertTriangle, Layers } from "lucide-react";
import { toast } from "sonner";

const UNITS = ["ml", "g", "unit", "each", "application"];
const empty = { name: "", category: "", product_type: "professional", cost_price: 0, retail_price: 0, pack_size: 1, unit: "ml", stock_qty: 0, low_stock_threshold: 2 };

export default function Inventory() {
  const { business } = useAuth();
  const cur = business?.currency || "AUD";
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [bundles, setBundles] = useState([]);
  const emptyBundle = { name: "", category: "", discount_type: "percentage", discount_value: 0, active: true, components: [] };
  const [bform, setBform] = useState(emptyBundle);
  const [bEditId, setBEditId] = useState(null);
  const [bOpen, setBOpen] = useState(false);
  const bset = (k, v) => setBform((f) => ({ ...f, [k]: v }));
  const retailProducts = useMemo(() => items.filter((p) => p.product_type === "retail"), [items]);
  const loadBundles = () => api.get("/product-bundles").then(({ data }) => setBundles(data)).catch(() => {});

  const load = () => api.get("/products").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); loadBundles(); }, []);

  const lowStock = useMemo(() => items.filter((p) => p.low_stock), [items]);
  const pricePerUnit = (form.cost_price || 0) / (form.pack_size || 1);
  const marginAmt = (parseFloat(form.retail_price) || 0) - (parseFloat(form.cost_price) || 0);
  const marginPct = (parseFloat(form.retail_price) || 0) > 0 ? (marginAmt / parseFloat(form.retail_price)) * 100 : 0;

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ ...empty, ...p }); setEditId(p.id); setOpen(true); };
  const save = async () => {
    if (!form.name.trim()) { toast.error("Product name required"); return; }
    const payload = {
      name: form.name.trim(), category: form.category, unit: form.unit, product_type: form.product_type,
      cost_price: parseFloat(form.cost_price) || 0, retail_price: parseFloat(form.retail_price) || 0,
      pack_size: parseFloat(form.pack_size) || 1, stock_qty: parseFloat(form.stock_qty) || 0,
      low_stock_threshold: parseFloat(form.low_stock_threshold) || 0,
    };
    try {
      if (editId) await api.put(`/products/${editId}`, payload);
      else await api.post("/products", payload);
      setOpen(false); load(); toast.success("Product saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save"); }
  };
  const remove = async (p) => { try { await api.delete(`/products/${p.id}`); load(); toast.success("Removed"); } catch { toast.error("Failed"); } };

  const openNewBundle = () => { setBform(emptyBundle); setBEditId(null); setBOpen(true); };
  const openEditBundle = (b) => { setBform({ name: b.name, category: b.category || "", discount_type: b.discount_type || "percentage", discount_value: b.discount_value || 0, active: b.active !== false, components: (b.components || []).map((c) => ({ product_id: c.product_id, qty: c.qty })) }); setBEditId(b.id); setBOpen(true); };
  const addComp = () => setBform((f) => ({ ...f, components: [...f.components, { product_id: retailProducts[0]?.id || "", qty: 1 }] }));
  const saveBundle = async () => {
    if (!bform.name.trim()) { toast.error("Bundle name required"); return; }
    const comps = bform.components.filter((c) => c.product_id && parseFloat(c.qty) > 0).map((c) => ({ product_id: c.product_id, qty: parseFloat(c.qty) }));
    if (comps.length === 0) { toast.error("Add at least one product to the bundle"); return; }
    const payload = { name: bform.name.trim(), category: bform.category, discount_type: bform.discount_type, discount_value: parseFloat(bform.discount_value) || 0, active: bform.active, components: comps };
    try {
      if (bEditId) await api.put(`/product-bundles/${bEditId}`, payload);
      else await api.post("/product-bundles", payload);
      setBOpen(false); loadBundles(); toast.success("Bundle saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save"); }
  };
  const removeBundle = async (b) => { try { await api.delete(`/product-bundles/${b.id}`); loadBundles(); toast.success("Removed"); } catch { toast.error("Failed"); } };
  const prodById = (id) => retailProducts.find((x) => x.id === id);
  const bundleCompCost = bform.components.reduce((s, c) => s + (prodById(c.product_id)?.cost_price || 0) * (parseFloat(c.qty) || 0), 0);
  const bundleCompValue = bform.components.reduce((s, c) => s + (prodById(c.product_id)?.retail_price || 0) * (parseFloat(c.qty) || 0), 0);
  const bundleMakes = bform.components.length ? Math.min(...bform.components.map((c) => { const p = prodById(c.product_id); const q = parseFloat(c.qty) || 1; return p ? Math.floor((p.stock_qty || 0) / q) : 0; })) : 0;
  const bundleDiscountAmt = bform.discount_type === "percentage"
    ? bundleCompValue * Math.min(parseFloat(bform.discount_value) || 0, 100) / 100
    : bform.discount_type === "fixed"
    ? Math.min(parseFloat(bform.discount_value) || 0, bundleCompValue)
    : 0;
  const bundlePrice = Math.max(0, bundleCompValue - bundleDiscountAmt);
  const bundleProfit = bundlePrice - bundleCompCost;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight">Inventory</h1>
          <p className="text-muted-foreground mt-1">Products, cost per unit & reorder alerts</p>
        </div>
        <Button className="rounded-lg" onClick={openNew} data-testid="add-product-button"><Plus className="h-4 w-4 mr-1.5" /> Add product</Button>
      </div>

      {/* Low stock reorder report */}
      {lowStock.length > 0 && (
        <Card className="rounded-2xl border border-amber-300 bg-amber-50 p-5 mb-6" data-testid="low-stock-report">
          <div className="flex items-center gap-2 mb-3 text-amber-700">
            <AlertTriangle className="h-4 w-4" />
            <h3 className="font-heading font-semibold">Reorder needed ({lowStock.length})</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {lowStock.map((p) => (
              <span key={p.id} className="px-3 py-1.5 rounded-full bg-white border border-amber-200 text-sm" data-testid={`low-stock-${p.id}`}>
                {p.name} — <strong className="tabular-nums">{p.stock_qty}</strong> left
              </span>
            ))}
          </div>
        </Card>
      )}

      {items.length === 0 ? (
        <Card className="rounded-2xl border bg-card p-12 text-center text-muted-foreground" data-testid="inventory-empty">
          <Package className="h-8 w-8 mx-auto mb-3 opacity-50" />
          No products yet. Add your first product to track cost per unit and stock.
        </Card>
      ) : (
        <Card className="rounded-2xl border bg-card shadow-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="p-3 font-mono-label text-[11px] uppercase tracking-wider">Product</th>
                <th className="p-3 font-mono-label text-[11px] uppercase tracking-wider">Cost price</th>
                <th className="p-3 font-mono-label text-[11px] uppercase tracking-wider">Pack size</th>
                <th className="p-3 font-mono-label text-[11px] uppercase tracking-wider">Price / unit</th>
                <th className="p-3 font-mono-label text-[11px] uppercase tracking-wider">Retail</th>
                <th className="p-3 font-mono-label text-[11px] uppercase tracking-wider">Margin</th>
                <th className="p-3 font-mono-label text-[11px] uppercase tracking-wider">Stock</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className={`border-b border-border/60 ${p.low_stock ? "bg-amber-50/60" : ""}`} data-testid={`product-row-${p.id}`}>
                  <td className="p-3">
                    <div className="font-medium flex items-center gap-2">{p.name}
                      <span className={`text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded-full ${p.product_type === "retail" ? "bg-secondary/15 text-secondary" : "bg-primary/12 text-primary"}`} data-testid={`product-type-${p.id}`}>{p.product_type === "retail" ? "Retail" : "Pro"}</span>
                    </div>
                    {p.category && <div className="text-xs text-muted-foreground">{p.category}</div>}
                  </td>
                  <td className="p-3 tabular-nums">{fmtCurrency(p.cost_price, cur, 2)}</td>
                  <td className="p-3 tabular-nums">{p.pack_size} {p.unit}</td>
                  <td className="p-3 tabular-nums font-medium text-primary" data-testid={`product-ppu-${p.id}`}>{fmtCurrency(p.price_per_unit, cur, 4)}/{p.unit}</td>
                  <td className="p-3 tabular-nums">{p.retail_price ? fmtCurrency(p.retail_price, cur, 2) : "—"}</td>
                  <td className="p-3 tabular-nums" data-testid={`product-margin-${p.id}`}>
                    {p.product_type === "retail" && p.retail_price ? (
                      <span className={p.margin_pct < 0 ? "text-destructive font-medium" : "text-emerald-600 font-medium"}>
                        {fmtCurrency(p.profit, cur, 2)}<span className="text-xs text-muted-foreground ml-1">· {p.margin_pct}%</span>
                      </span>
                    ) : "—"}
                  </td>
                  <td className="p-3 tabular-nums">
                    <span className={p.low_stock ? "text-amber-600 font-semibold" : ""}>{p.stock_qty}</span>
                    {p.low_stock && <span className="ml-1 text-[10px] uppercase text-amber-600">low</span>}
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(p)} className="text-muted-foreground hover:text-primary mr-3" data-testid={`product-edit-${p.id}`}><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => remove(p)} className="text-muted-foreground hover:text-destructive" data-testid={`product-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Bundle packs */}
      <div className="flex flex-wrap items-center justify-between gap-3 mt-10 mb-4">
        <div>
          <h2 className="font-heading font-bold text-xl flex items-center gap-2"><Layers className="h-5 w-5 text-secondary" />Bundle packs</h2>
          <p className="text-muted-foreground text-sm mt-0.5">Sell several retail products as one pack — stock is deducted per product automatically.</p>
        </div>
        <Button variant="outline" className="rounded-lg" onClick={openNewBundle} data-testid="add-bundle-button" disabled={retailProducts.length === 0}><Plus className="h-4 w-4 mr-1.5" /> Add bundle</Button>
      </div>
      {retailProducts.length === 0 ? (
        <Card className="rounded-2xl border bg-card p-6 text-center text-muted-foreground text-sm" data-testid="bundles-need-retail">Add at least one <strong>retail</strong> product above before creating a bundle pack.</Card>
      ) : bundles.length === 0 ? (
        <Card className="rounded-2xl border bg-card p-8 text-center text-muted-foreground" data-testid="bundles-empty">
          <Layers className="h-7 w-7 mx-auto mb-3 opacity-50" />
          No bundle packs yet. Group retail products into a pack (e.g. a skincare set) and sell it at a set price.
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {bundles.map((b) => (
            <Card key={b.id} className="rounded-2xl border bg-card p-4 shadow-sm" data-testid={`bundle-card-${b.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-heading font-semibold truncate">{b.name}</div>
                  {b.category && <div className="text-xs text-muted-foreground">{b.category}</div>}
                </div>
                <div className="text-right shrink-0">
                  {b.discount_amount > 0 && <div className="text-[10px] text-muted-foreground line-through tabular-nums">{fmtCurrency(b.components_value, cur, 0)}</div>}
                  <div className="font-heading font-bold tabular-nums">{fmtCurrency(b.price, cur, 2)}</div>
                  <div className={`text-[11px] font-medium ${b.margin_pct < 0 ? "text-destructive" : "text-emerald-600"}`}>{fmtCurrency(b.profit, cur, 0)} · {b.margin_pct}%</div>
                </div>
              </div>
              <div className="mt-2.5 space-y-1">
                {(b.component_detail || []).map((c, i) => (
                  <div key={i} className="flex justify-between text-xs" data-testid={`bundle-${b.id}-comp-${i}`}>
                    <span className={c.missing ? "text-destructive" : ""}>{c.qty}× {c.name}</span>
                    <span className="text-muted-foreground tabular-nums">{c.stock_qty} in stock</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between">
                <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${b.stock_available <= 0 ? "bg-amber-100 text-amber-700" : "bg-emerald-50 text-emerald-700"}`} data-testid={`bundle-${b.id}-makes`}>
                  {b.stock_available <= 0 ? "Out of stock" : `Makes ${b.stock_available} from stock`}
                </span>
                <div className="whitespace-nowrap">
                  <button onClick={() => openEditBundle(b)} className="text-muted-foreground hover:text-primary mr-3" data-testid={`bundle-edit-${b.id}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => removeBundle(b)} className="text-muted-foreground hover:text-destructive" data-testid={`bundle-delete-${b.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={bOpen} onOpenChange={setBOpen}>
        <DialogContent className="max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{bEditId ? "Edit bundle pack" : "New bundle pack"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <Input placeholder="Bundle name (e.g. Glow Skincare Set)" value={bform.name} onChange={(e) => bset("name", e.target.value)} className="h-11 rounded-lg" data-testid="bundle-name" />
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-muted-foreground">Category</label><Input placeholder="Optional" value={bform.category} onChange={(e) => bset("category", e.target.value)} className="h-11 rounded-lg" data-testid="bundle-category" /></div>
              <div>
                <label className="text-xs text-muted-foreground">Discount type</label>
                <Select value={bform.discount_type} onValueChange={(v) => bset("discount_type", v)}>
                  <SelectTrigger className="h-11 rounded-lg" data-testid="bundle-discount-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percent off (%)</SelectItem>
                    <SelectItem value="fixed">Dollar off ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">{bform.discount_type === "fixed" ? "Dollars off the combined price" : "Percent off the combined price"}</label>
              <div className="relative">
                {bform.discount_type === "fixed" && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>}
                <Input type="number" min="0" value={bform.discount_value} onChange={(e) => bset("discount_value", e.target.value)} className={`h-11 rounded-lg tabular-nums ${bform.discount_type === "fixed" ? "pl-7" : "pr-7"}`} data-testid="bundle-discount-value" />
                {bform.discount_type === "percentage" && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>}
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Products in this bundle</label>
              <div className="space-y-2 mt-1">
                {bform.components.map((c, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`bundle-comp-row-${i}`}>
                    <Select value={c.product_id} onValueChange={(v) => setBform((f) => ({ ...f, components: f.components.map((x, j) => j === i ? { ...x, product_id: v } : x) }))}>
                      <SelectTrigger className="h-10 rounded-lg flex-1" data-testid={`bundle-comp-select-${i}`}><SelectValue placeholder="Product" /></SelectTrigger>
                      <SelectContent>{retailProducts.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {fmtCurrency(p.retail_price, cur, 2)} · {p.stock_qty} in stock</SelectItem>)}</SelectContent>
                    </Select>
                    <Input type="number" min="1" value={c.qty} onChange={(e) => setBform((f) => ({ ...f, components: f.components.map((x, j) => j === i ? { ...x, qty: e.target.value } : x) }))} className="h-10 rounded-lg w-20 tabular-nums" data-testid={`bundle-comp-qty-${i}`} />
                    <button onClick={() => setBform((f) => ({ ...f, components: f.components.filter((_, j) => j !== i) }))} className="h-10 w-10 rounded-lg border flex items-center justify-center text-muted-foreground hover:text-destructive shrink-0"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                <button onClick={addComp} className="text-sm text-primary flex items-center gap-1 hover:underline" data-testid="bundle-add-comp"><Plus className="h-4 w-4" />Add product</button>
              </div>
            </div>
            <div className="rounded-lg bg-muted/50 px-3 py-2.5 text-sm space-y-1.5" data-testid="bundle-preview">
              <div className="flex justify-between"><span className="text-muted-foreground">Individually worth</span><span className="tabular-nums">{fmtCurrency(bundleCompValue, cur, 2)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Discount</span><span className="tabular-nums text-secondary">− {fmtCurrency(bundleDiscountAmt, cur, 2)}</span></div>
              <div className="flex justify-between font-medium"><span>Bundle price</span><span className="tabular-nums font-heading font-bold" data-testid="bundle-price-preview">{fmtCurrency(bundlePrice, cur, 2)}</span></div>
              <div className="flex justify-between border-t border-border/60 pt-1.5"><span className="text-muted-foreground">Cost to you</span><span className="tabular-nums">{fmtCurrency(bundleCompCost, cur, 2)}</span></div>
              <div className="flex justify-between font-medium"><span className={bundleProfit < 0 ? "text-destructive" : "text-emerald-700"}>Your profit</span><span className={`tabular-nums ${bundleProfit < 0 ? "text-destructive" : "text-emerald-700"}`}>{fmtCurrency(bundleProfit, cur, 2)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Makes from current stock</span><span className="tabular-nums font-medium">{bundleMakes}</span></div>
            </div>
          </div>
          <DialogFooter><Button onClick={saveBundle} className="rounded-lg" data-testid="bundle-save">Save bundle</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit product" : "Add product"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <Input placeholder="Product name" value={form.name} onChange={(e) => set("name", e.target.value)} className="h-11 rounded-lg" data-testid="product-name" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground">Use</label>
                <Select value={form.product_type} onValueChange={(v) => set("product_type", v)}>
                  <SelectTrigger className="h-11 rounded-lg" data-testid="product-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="professional">Professional (back-bar)</SelectItem>
                    <SelectItem value="retail">Retail (sold to clients)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Category</label>
                <Input placeholder="Optional" value={form.category} onChange={(e) => set("category", e.target.value)} className="h-11 rounded-lg" data-testid="product-category" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-muted-foreground">Cost price (per pack)</label><Input type="number" min="0" value={form.cost_price} onChange={(e) => set("cost_price", e.target.value)} className="h-11 rounded-lg" data-testid="product-cost-price" /></div>
              <div><label className="text-xs text-muted-foreground">Retail price</label><Input type="number" min="0" value={form.retail_price} onChange={(e) => set("retail_price", e.target.value)} className="h-11 rounded-lg" data-testid="product-retail-price" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-muted-foreground">Pack size</label><Input type="number" min="0" value={form.pack_size} onChange={(e) => set("pack_size", e.target.value)} className="h-11 rounded-lg" data-testid="product-pack-size" /></div>
              <div>
                <label className="text-xs text-muted-foreground">Unit</label>
                <Select value={form.unit} onValueChange={(v) => set("unit", v)}>
                  <SelectTrigger className="h-11 rounded-lg" data-testid="product-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="rounded-lg bg-muted/50 px-3 py-2 text-sm flex items-center justify-between">
              <span className="text-muted-foreground">Price per unit</span>
              <span className="font-heading font-bold text-primary tabular-nums" data-testid="product-ppu-preview">{fmtCurrency(pricePerUnit, cur, 4)}/{form.unit}</span>
            </div>
            {form.product_type === "retail" && (parseFloat(form.retail_price) || 0) > 0 && (
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm flex items-center justify-between" data-testid="product-margin-preview">
                <span className="text-emerald-700">Profit margin</span>
                <span className={`font-heading font-bold tabular-nums ${marginAmt < 0 ? "text-destructive" : "text-emerald-700"}`}>{fmtCurrency(marginAmt, cur, 2)} · {marginPct.toFixed(1)}%</span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-muted-foreground">Stock on hand</label><Input type="number" min="0" value={form.stock_qty} onChange={(e) => set("stock_qty", e.target.value)} className="h-11 rounded-lg" data-testid="product-stock" /></div>
              <div><label className="text-xs text-muted-foreground">Reorder at</label><Input type="number" min="0" value={form.low_stock_threshold} onChange={(e) => set("low_stock_threshold", e.target.value)} className="h-11 rounded-lg" data-testid="product-threshold" /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-lg" data-testid="product-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
